// src/funciones/construirRutaAprendizaje.js

/*
==========================================================
YUTAKE

Ruta de aprendizaje de un componente

Ordena los temas de un componente según el árbol de
prerrequisitos (materias_temas.Prerrequisitos) y les asigna
un estado de ruta usando ÚNICAMENTE los estados que entrega
el motor oficial (estadoAcademico.desglose).

No evalúa conocimiento. No consulta Firestore.
Es una función pura: misma entrada, misma salida.

Pensada para uso mixto: hoy la usa el profesor; luego
puede reemplazar el orden de temas en las vistas del
estudiante.
==========================================================
*/

// Mismo valor por defecto que usa motorEvaluacion para
// marcar un estado como "suficiente" (nivel >= 3).
const NIVEL_OBJETIVO_MOTOR = 3;

const aId = (valor) => Number(valor);

const estaSuperado = (tema) =>
    !!tema &&
    tema.totalEvidencias > 0 &&
    tema.nivel >= NIVEL_OBJETIVO_MOTOR;

const RUTA_VACIA = {
    etapas: [],
    siguientes: [],
    resumen: {
        total: 0,
        superados: 0,
        enCurso: 0,
        listos: 0,
        pendientes: 0
    }
};

const construirRutaAprendizaje = ({ desglose, componenteId }) => {

    const componente = desglose?.componentes?.[componenteId];

    if (!componente) {
        return RUTA_VACIA;
    }

    const temasPorId = {};

    Object.values(desglose.temas || {}).forEach(tema => {
        temasPorId[aId(tema.id)] = tema;
    });

    const idsComponente = new Set(
        (componente.temas || []).map(aId)
    );

    // Etapa = profundidad en el árbol de prerrequisitos
    // dentro del componente (0 = no requiere nada previo).

    const memo = {};

    const profundidad = (id, visitando = new Set()) => {

        if (memo[id] !== undefined) return memo[id];

        // Protección ante ciclos en los datos
        if (visitando.has(id)) return 0;

        visitando.add(id);

        const internos = (temasPorId[id]?.prerrequisitos || [])
            .map(aId)
            .filter(p => p !== id && idsComponente.has(p));

        const valor = internos.length === 0
            ? 0
            : 1 + Math.max(...internos.map(p => profundidad(p, visitando)));

        visitando.delete(id);

        memo[id] = valor;

        return valor;
    };

    const nodos = [...idsComponente]
        .map(id => temasPorId[id])
        .filter(Boolean)
        .map(tema => {

            const idTema = aId(tema.id);

            const prerrequisitos = (tema.prerrequisitos || [])
                .map(aId)
                .filter(p => p !== idTema)
                .map(p => ({
                    id: p,
                    nombre: temasPorId[p]?.nombre ?? `Tema ${p}`,
                    superado: estaSuperado(temasPorId[p]),
                    externo: !idsComponente.has(p)
                }));

            const prerrequisitosPendientes =
                prerrequisitos.filter(p => !p.superado);

            let estadoRuta;

            if (tema.totalEvidencias > 0) {
                estadoRuta = estaSuperado(tema) ? "superado" : "en_curso";
            } else {
                estadoRuta = prerrequisitosPendientes.length === 0
                    ? "listo"
                    : "pendiente";
            }

            return {
                ...tema,
                etapa: profundidad(idTema),
                estadoRuta,
                prerrequisitos,
                prerrequisitosPendientes
            };
        });

    // Agrupar por etapa

    const etapasMap = {};

    nodos.forEach(nodo => {
        if (!etapasMap[nodo.etapa]) etapasMap[nodo.etapa] = [];
        etapasMap[nodo.etapa].push(nodo);
    });

    const etapas = Object.keys(etapasMap)
        .map(Number)
        .sort((a, b) => a - b)
        .map((profundidadEtapa, indice) => ({
            numero: indice + 1,
            temas: etapasMap[profundidadEtapa].sort((a, b) =>
                String(a.nombre).localeCompare(String(b.nombre))
            )
        }));

    // Siguientes pasos: lo que está en progreso o listo,
    // empezando por las etapas más tempranas.

    const prioridadEstado = { en_curso: 0, listo: 1 };

    const siguientes = etapas
        .flatMap(etapa => etapa.temas.map(t => ({ ...t, numeroEtapa: etapa.numero })))
        .filter(t => t.estadoRuta === "en_curso" || t.estadoRuta === "listo")
        .sort((a, b) =>
            a.numeroEtapa - b.numeroEtapa ||
            prioridadEstado[a.estadoRuta] - prioridadEstado[b.estadoRuta]
        )
        .slice(0, 3);

    const contar = (estado) =>
        nodos.filter(n => n.estadoRuta === estado).length;

    return {
        etapas,
        siguientes,
        resumen: {
            total: nodos.length,
            superados: contar("superado"),
            enCurso: contar("en_curso"),
            listos: contar("listo"),
            pendientes: contar("pendiente")
        }
    };
};

export default construirRutaAprendizaje;